import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { FormProvider } from '@onesource/components';
import { TUpdateProDirectory } from '@onesource/schemas';
import {
  useGetCitiesQuery,
  useGetCountriesQuery,
  useGetExpertiseQuery,
  useGetProDirectoryPendingRevisionQuery,
  useGetProDirectoryQuery,
  useGetProfessionsQuery,
  useGetStatesQuery,
  useUpdateProDirectoryMutation,
} from '@onesource/services';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, useWatch } from 'react-hook-form';
import { SHOW_DEV_TOOLS } from 'src/config';

import { ProDirectoryRevisionsDialog } from './revisions-dialog';
import { ProDirectoryStepBusiness } from './step-business';
import { ProDirectoryStepFooter } from './step-footer';
import { ProDirectoryStepPersonal } from './step-personal';
import { ProDirectoryStepPreview } from './step-preview';
import { ProDirectoryStepTeaching } from './step-teaching';
import { ProDirectoryStepperHeader } from './stepper-header';
import {
  DEFAULT_LANGUAGES_TAUGHT,
  EMPTY_FORM_VALUES,
  formValuesFromProDirectory,
  formValuesFromRevisionData,
  getFieldError,
  getMissingFields,
  getMissingFieldsWithStep,
  getValidatedFieldStep,
  isFormDirty,
  isOtherProfessionSelected,
  isStepComplete,
  normalizePhone,
  normalizeWebsiteUrl,
  ProFormSchema,
  STEP_FIELDS,
  STEPS,
  TFormIssue,
  TProFormValues,
  VALIDATED_FIELD_LABELS,
} from './steps';
import { ProDirectoryTabSkeleton } from './tab-skeleton';

type TProfileSocials = {
  facebook: string;
  instagram: string;
  youtube: string;
  twitter: string;
  linkedin: string;
  tiktok: string;
};

export type TProDirectoryTabProps = {
  profileFullName: string;
  profileBio: string;
  profilePhone: string;
  profileSocials: TProfileSocials;
  profileAvatar: string;
  stepperSlot?: HTMLDivElement | null;
  isTabActive: boolean;
};

export const ProDirectoryTab: FC<TProDirectoryTabProps> = ({
  profileFullName,
  profileBio,
  profilePhone,
  profileSocials,
  profileAvatar,
  stepperSlot,
  isTabActive,
}) => {
  const { data: proDirectory, isLoading } = useGetProDirectoryQuery();
  const { data: pendingRevision } = useGetProDirectoryPendingRevisionQuery();
  const { mutateAsync, isPending } = useUpdateProDirectoryMutation();
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [copyFullName, setCopyFullName] = useState(false);
  const [copyBio, setCopyBio] = useState(false);
  const [copyPhone, setCopyPhone] = useState(false);
  const [copySocials, setCopySocials] = useState(false);

  const methods = useForm<TProFormValues>({
    resolver: zodResolver(ProFormSchema),
    defaultValues: {
      slug: '',
      bio: '',
      profession: [],
      otherProfession: '',
      companyName: '',
      companyEmail: '',
      professionalWebsite: '',
      offerings: [],
      teachingFormat: 'both',
      expertise: [],
      languagesTaught: DEFAULT_LANGUAGES_TAUGHT,
      casualImage: '',
      landscapeImage: '',
      fullName: '',
      title: '',
      phone: '',
      businessStreet: '',
      businessCountryId: null,
      businessStateId: null,
      businessCityId: null,
      certificates: '',
      socials: {
        facebook: '',
        instagram: '',
        youtube: '',
        twitter: '',
        linkedin: '',
        tiktok: '',
      },
    },
    mode: 'all',
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = methods;

  const watchedValues = useWatch({ control });
  const watchedCountryId = useWatch({ control, name: 'businessCountryId' });
  const watchedStateId = useWatch({ control, name: 'businessStateId' });
  const values = watchedValues as Partial<TProFormValues>;

  const missingFields = getMissingFields(values);
  const isComplete = missingFields.length === 0;

  const fullNameMatchesProfile = !!profileFullName && watchedValues.fullName === profileFullName;
  const bioMatchesProfile = !!profileBio && watchedValues.bio === profileBio;
  const phoneMatchesProfile = !!profilePhone && watchedValues.phone === profilePhone;
  const socialsMatchProfile =
    !!Object.values(profileSocials).some(Boolean) &&
    watchedValues.socials?.facebook === profileSocials.facebook &&
    watchedValues.socials?.instagram === profileSocials.instagram &&
    watchedValues.socials?.youtube === profileSocials.youtube &&
    watchedValues.socials?.twitter === profileSocials.twitter &&
    watchedValues.socials?.linkedin === profileSocials.linkedin &&
    watchedValues.socials?.tiktok === profileSocials.tiktok;

  // Disable copy checkbox only when: no profile data, OR values already match but copy is NOT active
  // When copy IS active (checked), always allow unchecking
  const disableCopyFullName = !profileFullName || (!copyFullName && fullNameMatchesProfile);
  const disableCopyBio = !profileBio || (!copyBio && bioMatchesProfile);
  const disableCopyPhone = !profilePhone || (!copyPhone && phoneMatchesProfile);
  const disableCopySocials = !Object.values(profileSocials).some(Boolean) || (!copySocials && socialsMatchProfile);

  const { data: countries = [] } = useGetCountriesQuery();
  const { data: states = [] } = useGetStatesQuery({ countryId: watchedCountryId });
  const { data: cities = [] } = useGetCitiesQuery({ stateId: watchedStateId });
  const { data: professions = [] } = useGetProfessionsQuery();
  const { data: expertiseItems = [] } = useGetExpertiseQuery();

  const prevCountryIdRef = useRef<number | null>(null);

  useEffect(() => {
    const prevId = prevCountryIdRef.current;
    prevCountryIdRef.current = watchedCountryId ?? null;
    if (!watchedCountryId || watchedCountryId === prevId) {
      return;
    }
    const currentPhone = getValues('phone') ?? '';
    // Only set code if phone is empty or contains only a country code (no subscriber number)
    const isCodeOnly = /^\+\d{1,4}\s*$/.test(currentPhone.trim()) || currentPhone.trim() === '';
    if (!isCodeOnly) {
      return;
    }
    const country = countries.find(c => c.id === watchedCountryId);
    if (country?.phonecode) {
      setValue('phone', `+${country.phonecode}`);
    }
  }, [watchedCountryId, countries, setValue, getValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // The loaded baseline the form was hydrated from. Used both to seed RHF and to
  // compute a reliable "is the form actually different" flag below.
  const baselineValues = useMemo<TProFormValues>(() => {
    if (pendingRevision) {
      return formValuesFromRevisionData(pendingRevision.data as Record<string, unknown>);
    }
    if (proDirectory) {
      return formValuesFromProDirectory(proDirectory as unknown as Record<string, unknown>);
    }
    return EMPTY_FORM_VALUES;
  }, [proDirectory, pendingRevision]);

  // Real dirty check: diff current values against the loaded baseline so that
  // editing a field and reverting it clears "dirty" (RHF's isDirty can stick).
  const isActuallyDirty = isFormDirty(values, baselineValues);

  useEffect(() => {
    if (pendingRevision || proDirectory) {
      reset(baselineValues);
    }
  }, [baselineValues, proDirectory, pendingRevision, reset]);

  useEffect(() => {
    if (copyFullName) {
      setValue('fullName', profileFullName, { shouldDirty: true });
    }
  }, [copyFullName, profileFullName, setValue]);

  useEffect(() => {
    if (copyBio) {
      setValue('bio', profileBio, { shouldDirty: true });
    }
  }, [copyBio, profileBio, setValue]);

  useEffect(() => {
    if (copyPhone) {
      setValue('phone', profilePhone, { shouldDirty: true });
    }
  }, [copyPhone, profilePhone, setValue]);

  useEffect(() => {
    if (copySocials) {
      setValue('socials.facebook', profileSocials.facebook, { shouldDirty: true });
      setValue('socials.instagram', profileSocials.instagram, { shouldDirty: true });
      setValue('socials.youtube', profileSocials.youtube, { shouldDirty: true });
      setValue('socials.twitter', profileSocials.twitter, { shouldDirty: true });
      setValue('socials.linkedin', profileSocials.linkedin, { shouldDirty: true });
      setValue('socials.tiktok', profileSocials.tiktok, { shouldDirty: true });
    }
  }, [copySocials, profileSocials, setValue]);

  // Scroll step body back to top on step change
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeStep]);

  // Tab stays mounted (display:none) when switching away. Reset to the first
  // step on leave (while hidden) so re-entry doesn't flash the last step.
  useEffect(() => {
    if (!isTabActive) {
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabActive]);

  const buildPayload = (data: Partial<TProFormValues>, isDraft: boolean): TUpdateProDirectory => ({
    isDraft,
    bio: data.bio,
    profession: data.profession,
    // Only meaningful alongside the "Others" profession — clear it otherwise so a
    // stale value can't survive the user deselecting "Others".
    otherProfession: isOtherProfessionSelected(data.profession) ? data.otherProfession : null,
    companyName: data.companyName,
    companyEmail: data.companyEmail,
    professionalWebsite: normalizeWebsiteUrl(data.professionalWebsite),
    offerings: data.offerings,
    teachingFormat: data.teachingFormat,
    expertise: data.expertise,
    languagesTaught: data.languagesTaught,
    casualImage: data.casualImage,
    landscapeImage: data.landscapeImage,
    fullName: data.fullName,
    title: data.title,
    phone: normalizePhone(data.phone),
    businessStreet: data.businessStreet,
    businessCountryId: data.businessCountryId,
    businessStateId: data.businessStateId,
    businessCityId: data.businessCityId,
    certificates: data.certificates,
    socials: data.socials,
  });

  const onSubmitForReview = handleSubmit(async data => {
    await mutateAsync(buildPayload(data, false));
  });

  const onSaveDraft = useCallback(async () => {
    await mutateAsync(buildPayload(getValues(), true));
    // buildPayload is a pure mapper over its argument — safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutateAsync, getValues]);

  // Step completion: required fields filled + no validation errors within the step's fields
  const hasStepErrors = (step: number): boolean =>
    STEP_FIELDS[step].some(path => {
      let node: unknown = errors;
      for (const part of path.split('.')) {
        if (!node || typeof node !== 'object') {
          return false;
        }
        node = (node as Record<string, unknown>)[part];
      }
      return !!node;
    });

  // Recomputed on every keystroke (useWatch above subscribes to all fields), so
  // memoise on the step-completion booleans rather than the array identity —
  // otherwise the memoised header re-renders on every character typed.
  const baseCompleted = [0, 1, 2].map(step => isStepComplete(values, step) && !hasStepErrors(step));
  const [step0Complete, step1Complete, step2Complete] = baseCompleted;
  const completedSteps = useMemo(
    () => [step0Complete, step1Complete, step2Complete, step0Complete && step1Complete && step2Complete],
    [step0Complete, step1Complete, step2Complete],
  );

  const isStepEnabled = useCallback((_step: number): boolean => true, []);

  const handleStepClick = useCallback(
    (step: number) => {
      if (isStepEnabled(step)) {
        setActiveStep(step);
      }
    },
    [isStepEnabled],
  );

  const handleContinue = useCallback(async () => {
    await trigger(STEP_FIELDS[activeStep]);
    setActiveStep(step => Math.min(step + 1, STEPS.length - 1));
  }, [trigger, activeStep]);

  const handleBack = useCallback(() => setActiveStep(step => Math.max(step - 1, 0)), []);
  const handleRevisionsOpen = useCallback(() => setRevisionsOpen(true), []);

  // Format errors (e.g. a malformed Business Website) on fields that aren't
  // required — surfaced separately so they read as "fix this" rather than
  // "this is required", and so they don't block Continue/Submit like a missing
  // required field does.
  //
  // Recomputed every render (not memoised on `errors`) — RHF mutates the
  // `formState.errors` object in place for perf, so its reference doesn't
  // change when a field's error clears, and a `useMemo` keyed on it would show
  // stale messages after the user fixes the field.
  const validationIssues = (Object.keys(VALIDATED_FIELD_LABELS) as Array<keyof typeof VALIDATED_FIELD_LABELS>)
    .map(path => ({ path, message: getFieldError(errors, path)?.message }))
    .filter((issue): issue is { path: keyof typeof VALIDATED_FIELD_LABELS; message: string } => !!issue.message);

  // Every outstanding issue across all steps (not just the active one), for the
  // always-visible help popover next to Continue/Preview/Submit.
  const allIssues: TFormIssue[] = [
    ...getMissingFieldsWithStep(values).map(
      ({ label, step }): TFormIssue => ({ kind: 'missing', message: label, step }),
    ),
    ...validationIssues.map(
      ({ path, message }): TFormIssue => ({ kind: 'invalid', message, step: getValidatedFieldStep(path) }),
    ),
  ];

  if (isLoading) {
    return <ProDirectoryTabSkeleton />;
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ProDirectoryRevisionsDialog
        open={revisionsOpen}
        onClose={() => setRevisionsOpen(false)}
        approvedData={proDirectory ?? null}
      />

      {stepperSlot &&
        createPortal(
          <ProDirectoryStepperHeader
            activeStep={activeStep}
            completedSteps={completedSteps}
            onStepClick={handleStepClick}
            isStepEnabled={isStepEnabled}
          />,
          stepperSlot,
        )}

      <FormProvider
        methods={methods}
        onSubmit={onSubmitForReview}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          '& form': { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Top: progress + stepper nav (falls back inline when no slot, e.g. below the lg breakpoint) */}
          <Box sx={{ flexShrink: 0 }}>
            <LinearProgress sx={{ mb: 1.5, borderRadius: 1, visibility: isPending ? 'visible' : 'hidden' }} />
            {!stepperSlot && (
              <ProDirectoryStepperHeader
                activeStep={activeStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
                isStepEnabled={isStepEnabled}
              />
            )}
          </Box>

          {/* Middle: scrollable step body */}
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', mt: 2 }}>
            <Box sx={{ minHeight: '100%' }}>
              <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                <ProDirectoryStepPersonal
                  isPending={isPending}
                  profileFullName={profileFullName}
                  profileBio={profileBio}
                  profileAvatar={profileAvatar}
                  copyFullName={copyFullName}
                  copyBio={copyBio}
                  disableCopyFullName={disableCopyFullName}
                  disableCopyBio={disableCopyBio}
                  onCopyFullNameChange={setCopyFullName}
                  onCopyBioChange={setCopyBio}
                />
              </Box>

              <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                <ProDirectoryStepTeaching isPending={isPending} />
              </Box>

              <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
                <ProDirectoryStepBusiness
                  isPending={isPending}
                  countries={countries}
                  states={states}
                  cities={cities}
                  profilePhone={profilePhone}
                  profileSocials={profileSocials}
                  copyPhone={copyPhone}
                  copySocials={copySocials}
                  disableCopyPhone={disableCopyPhone}
                  disableCopySocials={disableCopySocials}
                  onCopyPhoneChange={setCopyPhone}
                  onCopySocialsChange={setCopySocials}
                />
              </Box>

              <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
                <ProDirectoryStepPreview
                  countries={countries}
                  states={states}
                  cities={cities}
                  professions={professions}
                  expertiseItems={expertiseItems}
                />
              </Box>
            </Box>
          </Box>

          {/* Bottom: actions */}

          <ProDirectoryStepFooter
            activeStep={activeStep}
            isPending={isPending}
            continueDisabled={isPending || (activeStep === 2 && !isComplete)}
            saveDisabled={isPending || !isActuallyDirty}
            submitDisabled={
              isPending ||
              // Block a no-op resubmit of an already-submitted revision: pending
              // (awaiting review) or approved (live) data loaded unchanged has
              // nothing new to submit. A draft (never submitted) or a rejected
              // revision (retry) may still be submitted as-is once complete.
              (!isActuallyDirty && (pendingRevision?.status === 'pending' || pendingRevision?.status === 'approved')) ||
              !isComplete
            }
            allIssues={allIssues}
            hasPendingRevision={!!pendingRevision}
            onRevisionsClick={handleRevisionsOpen}
            onBack={handleBack}
            onContinue={handleContinue}
            onSaveDraft={onSaveDraft}
          />
        </Box>

        {SHOW_DEV_TOOLS && <DevTool id="account-pro-directory" control={methods.control} />}
      </FormProvider>
    </Box>
  );
};
